import {SxProps, Theme} from '@mui/material';
import theme from 'src/theme';

/**
 * The plan's own card.
 *
 * The page used to open with a marketing panel borrowed from the contract-signing flow — centred,
 * with a Select button and a raspberry title — and printed the monthly fee twice inside it. This is
 * the same card every other detail page now opens with; what it keeps from the old one is the
 * raspberry, because on this page it says "tariff" rather than "careful".
 */
export const cardSx: SxProps<Theme> = {
    background: theme.palette.common.white,
    borderRadius: '14px',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)',
    overflow: 'hidden',
};

/** The plan's spine. Three pixels of the brand accent, the way a paper plan has a coloured edge. */
export const accentBarSx: SxProps<Theme> = {
    height: '3px',
    background: `linear-gradient(90deg, ${theme.palette.brand.main} 0%, ${theme.palette.brand.light} 100%)`,
};

export const headerSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    flexWrap: 'wrap',
    px: 3,
    // Taller than the section headers below it. This card is the page's subject; the sections under
    // it are about the subject, and the difference in height is what says so before anything is read.
    py: 5,
    background: 'linear-gradient(180deg, #FEF3F6 0%, #FFFFFF 100%)',
};

export const badgeSx: SxProps<Theme> = {
    width: '58px',
    height: '58px',
    borderRadius: '14px',
    background: `linear-gradient(135deg, #FFE8EB 0%, ${theme.palette.brand.light} 100%)`,
    color: theme.palette.brand.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {fontSize: '30px'},
};

/** The dark brand tone, not the mid one: at heading size the mid tone reads as a control. */
export const titleSx: SxProps<Theme> = {
    fontSize: '1.625rem',
    fontWeight: 600,
    letterSpacing: '-0.015em',
    color: theme.palette.brand.dark,
};

export const subtitleSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    color: theme.palette.neutral[500],
    mt: 0.25,
};

export const statusPillSx = (active: boolean): SxProps<Theme> => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
    fontSize: '0.6875rem',
    fontWeight: 600,
    px: 1.25,
    py: 0.5,
    borderRadius: '999px',
    whiteSpace: 'nowrap',
    background: active ? '#EAF6EC' : theme.palette.neutral[50],
    color: active ? theme.palette.success.dark : theme.palette.neutral[600],
    border: `1px solid ${active ? theme.palette.success.light : theme.palette.neutral[200]}`,
    '&::before': {
        content: '""',
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        background: active ? theme.palette.success.main : theme.palette.neutral[400],
    },
});

export const factsSx: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)'},
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
};

export const factSx: SxProps<Theme> = {
    px: 3,
    py: 3.5,
    borderRight: `1px solid ${theme.palette.neutral[100]}`,
    '&:last-of-type': {borderRight: 'none'},
};

export const factLabelSx: SxProps<Theme> = {
    fontSize: '0.625rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: theme.palette.neutral[500],
};

export const factValueSx: SxProps<Theme> = {
    fontSize: '0.875rem',
    fontWeight: 500,
    mt: 0.4,
};

/** The one figure that keeps the accent — it is what the plan charges, and it is printed once. */
export const feeSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'baseline',
    gap: 0.6,
    mt: 0.25,
};

export const feeCurrencySx: SxProps<Theme> = {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.brand.main,
};

export const feeAmountSx: SxProps<Theme> = {
    fontSize: '1.5rem',
    fontWeight: 600,
    letterSpacing: '-0.02em',
    lineHeight: 1,
    color: theme.palette.brand.main,
    fontVariantNumeric: 'tabular-nums',
};

export const feePeriodSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    color: theme.palette.neutral[500],
};
